/**
 * A child drawing a cat uses six strokes and means one cat. Strokes whose
 * bounding boxes nearly touch are welded into a single creature.
 */

import type { Stroke } from './stroke.ts';
import { bounds } from './geom.ts';

interface Box {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

function overlaps(a: Box, b: Box, gap: number): boolean {
  return a.minX - gap <= b.maxX && b.minX - gap <= a.maxX && a.minY - gap <= b.maxY && b.minY - gap <= a.maxY;
}

export function groupStrokes(strokes: Stroke[], gap = 26): Stroke[][] {
  const boxes = strokes.map((s) => bounds(s.points));
  const parent = strokes.map((_, i) => i);

  const find = (i: number): number => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]];
      i = parent[i];
    }
    return i;
  };
  const union = (a: number, b: number): void => {
    const ra = find(a);
    const rb = find(b);
    if (ra !== rb) parent[rb] = ra;
  };

  for (let i = 0; i < strokes.length; i++) {
    for (let j = i + 1; j < strokes.length; j++) {
      if (overlaps(boxes[i], boxes[j], gap)) union(i, j);
    }
  }

  const groups = new Map<number, Stroke[]>();
  strokes.forEach((s, i) => {
    const root = find(i);
    const list = groups.get(root);
    if (list) list.push(s);
    else groups.set(root, [s]);
  });
  return [...groups.values()];
}
